import {
  createRef,
  MouseEvent,
  PureComponent,
  ReactNode,
  RefObject,
  startTransition,
  useEffect,
  useState,
} from 'react'

import { Transition, FocusTrap } from '@headlessui/react'
import { clsx } from 'clsx'

import { BackgroundProvider } from '../BackgroundContext/index.js'
import { Divider } from '../Divider/index.js'
import { XIcon } from '../icons/index.js'
import { Container } from '../Layout/index.js'
import { Portal } from '../Portal/index.js'
import { setPropertyValue } from '../utils/css-utils.js'
import { FrameContext } from './FrameContext.js'
import { useMediaQuery } from './MediaQueryContext.js'
import { ResponsiveLogo } from './types.js'

interface MobileNavigationMarkupProps {
  mobileNavShowing: boolean
  handleNavigationDismiss: () => void
  content: JSX.Element
}
const MobileNavigationMarkup = ({
  mobileNavShowing,
  handleNavigationDismiss,
  content,
}: MobileNavigationMarkupProps) => {
  const [isMounted, setIsMounted] = useState(false)

  useEffect(() => {
    setIsMounted(true)
  }, [])

  if (!isMounted) {
    return null
  }

  return (
    <Portal>
      <Transition show={mobileNavShowing}>
        <Transition.Child
          enter="transition-opacity duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
          className="z-20 fixed inset-0 overflow-hidden"
        >
          <div
            className="absolute inset-0 bg-background-backdrop"
            onClick={handleNavigationDismiss}
            onTouchStart={handleNavigationDismiss}
          />
        </Transition.Child>

        <Transition.Child
          enter="ease-out duration-200 transform"
          enterFrom="-translate-x-full"
          enterTo="translate-x-0"
          leave="ease-in duration-200 transform"
          leaveFrom="translate-x-0"
          leaveTo="-translate-x-full"
          className="z-20 fixed inset-y-0 start-0 max-w-xs w-full bg-background px-3 py-4 overflow-y-auto"
          aria-modal="true"
          role="dialog"
        >
          <FocusTrap>
            <BackgroundProvider backgroundType="main">
              <button
                type="button"
                onClick={handleNavigationDismiss}
                tabIndex={0}
                className="mb-5"
                aria-label="Close"
              >
                <XIcon className="h-8 w-8" aria-hidden="true" />
              </button>
              {content}
            </BackgroundProvider>
          </FocusTrap>
        </Transition.Child>
      </Transition>
    </Portal>
  )
}
export interface FrameProps {
  document: Document
  /** Sets the logo for the TopBar, Navigation components */
  logo?: ResponsiveLogo
  /** The content to display inside the frame. */
  children?: ReactNode
  /** Accepts a top bar component that will be rendered at the top-most portion of an application frame */
  topBar?: ReactNode
  /** A enum property indicating whether the top bar and navigation should stick to top during scrolling
   * @default false
   */
  sticky?: 'top-bar' | 'top-bar-navigation' | 'none'
  /** Accepts a navigation component that will be rendered in the left sidebar of an application frame */
  navigation?: ReactNode
  navigationSecondary?: ReactNode
  fullWidth?: boolean
  /** A enum property indicating whether the top bar and navigation should be rendered
   * @default 'default'
   */
  layout?: 'default' | 'no-sidebar' | 'basic'
  /** A boolean property indicating whether the mobile navigation is currently visible
   * @default false
   */
  mobileSidebarOpen?: boolean
  /** Accepts a ref to the html anchor element you wish to focus when clicking the skip to content link */
  skipToContentTarget?: RefObject<HTMLAnchorElement>
  /** A callback function to handle clicking the mobile navigation dismiss button */
  onNavigationDismiss?(): void
}

type CombinedProps = FrameProps & {
  mediaQuery: ReturnType<typeof useMediaQuery>
}

interface State {
  skipFocused?: boolean
  topBarStuck?: boolean
  topOffset?: number
}

const APP_FRAME_MAIN = 'FrameMain'

export class FrameInner extends PureComponent<CombinedProps, State> {
  // eslint-disable-next-line react/state-in-constructor
  state: State = {
    skipFocused: false,
    topBarStuck: true,
    topOffset: 0,
  }

  private topBarRef = createRef<HTMLDivElement>()

  private styleObserver: MutationObserver | undefined

  private intersectionObserver: IntersectionObserver | undefined

  private headerSizeObserver: ResizeObserver | undefined

  constructor(props) {
    super(props)
    this.handleNavKeydown.bind(this)
  }

  componentDidMount() {
    if (typeof window !== 'undefined') {
      window.addEventListener('keydown', this.handleNavKeydown as any)

      this.styleObserver = new MutationObserver(mutations => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const style = mutations?.[0]?.target?.style
        const currentValue = style?.getPropertyValue('--frame-top-offset')
        const { topOffset } = this.state

        if (currentValue && currentValue !== topOffset) {
          startTransition(() => {
            this.setState({
              topOffset: parseInt(currentValue, 10),
            })
          })
        }
      })

      const { document } = this.props
      this.styleObserver.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['style'],
      })
    }

    if (this.topBarRef.current) {
      this.headerSizeObserver = new ResizeObserver(() => {
        const { document } = this.props
        if (this.topBarRef.current) {
          setPropertyValue(
            document,
            '--c-top-bar-height',
            `${this.topBarRef.current.offsetHeight}px`,
          )
        }
      })
      this.headerSizeObserver.observe(this.topBarRef.current)

      this.intersectionObserver = new IntersectionObserver(
        ([e]) => {
          startTransition(() => {
            this.setState({
              topBarStuck: e.intersectionRatio < 1,
            })
          })
        },
        {
          threshold: [1],
        },
      )
      this.intersectionObserver.observe(this.topBarRef.current)
    }
  }

  componentWillUnmount() {
    this.styleObserver?.disconnect()

    if (typeof window !== 'undefined') {
      window.removeEventListener('keydown', this.handleNavKeydown as any)
    }

    if (this.topBarRef?.current) {
      this.intersectionObserver?.unobserve(this.topBarRef.current)
      this.headerSizeObserver?.unobserve(this.topBarRef.current)
    }
  }

  render() {
    const { skipFocused, topOffset, topBarStuck } = this.state
    const {
      logo,
      children,
      navigation,
      navigationSecondary,
      topBar,
      mobileSidebarOpen = false,
      skipToContentTarget,
      mediaQuery: { isNavigationCollapsed },
      fullWidth = false,
      layout = 'default',
      sticky = 'none',
    } = this.props

    const mobileSidebarEnabled = isNavigationCollapsed

    const mobileNavShowing = mobileSidebarEnabled && mobileSidebarOpen

    const layoutContainsNavigation =
      !!navigation && ['no-header', 'default'].includes(layout)
    const layoutContainsMobileNavigation =
      layoutContainsNavigation && isNavigationCollapsed
    const layoutContainsTopbar =
      !!topBar && ['no-sidebar', 'default'].includes(layout)

    const stickyTopBar = sticky === 'top-bar' || sticky === 'top-bar-navigation'
    const stickyNavigation = sticky === 'top-bar-navigation'

    const trackTopBar = stickyNavigation && topOffset > 0

    const desktopNavigationMarkup = (
      <Container
        size="sm"
        direction="vertical"
        spacing="none"
        hide={{ default: true, lg: false }}
        key="NavContent"
        containerProps={{
          'aria-label': 'Navigation',
          className: clsx(
            stickyNavigation && [
              'sticky',
              layoutContainsTopbar && [
                'top-[var(--c-top-bar-height)]',
                !topBarStuck
                  ? 'max-h-[calc(100vh-var(--c-top-bar-height)-var(--frame-top-offset))]'
                  : 'max-h-[calc(100vh-var(--c-top-bar-height))]',
              ],
              'scrollbar-hide overscroll-contain',
            ],
          ),
        }}
      >
        {navigation}
      </Container>
    )

    const navigationSecondaryMarkup = navigationSecondary ? (
      <div className="flex flex-col w-full space-y-5 px-3">
        <Divider />
        <h4>Navigation</h4>
        {navigationSecondary}
      </div>
    ) : null

    const topBarMarkup = layoutContainsTopbar ? (
      <Container
        as="header"
        size="full"
        containerProps={{
          ref: this.topBarRef,
          role: 'banner',
          className: clsx(
            'bg-topbar text-content-on-topbar',
            '-ms-[calc(env(safe-area-inset-left))] ps-[calc(env(safe-area-inset-left))]',
            '-me-[calc(env(safe-area-inset-right))] pe-[calc(env(safe-area-inset-right))]',
            stickyTopBar &&
              'sticky z-20 inset-x-0 shadow border-b border-line-subdued',
            trackTopBar ? '-top-1' : 'top-0',
          ),
        }}
      >
        {topBar}
      </Container>
    ) : null

    const skipTarget = skipToContentTarget?.current
      ? skipToContentTarget.current.id
      : APP_FRAME_MAIN

    const skipMarkup = (
      <a
        className={clsx(
          'fixed top-4 start-4 z-30 p-1 bg-topbar text-content-on-topbar',
          skipFocused ? 'opacity-100' : 'opacity-0 pointer-events-none',
        )}
        href={`#${skipTarget}`}
        onFocus={this.handleFocus}
        onBlur={this.handleBlur}
        onClick={this.handleClick}
      >
        Skip to content
      </a>
    )

    const navigationAttributes = navigation
      ? {
          'data-has-navigation': true,
        }
      : {}

    const context = {
      logo,
      fullWidth,
    }

    return (
      <FrameContext.Provider value={context}>
        <Container
          size="full"
          spacing="none"
          containerProps={{
            className: clsx(
              'bg-background min-h-screen',
              'ms-[calc(env(safe-area-inset-left))]',
              'me-[calc(env(safe-area-inset-right))]',
            ),
          }}
        >
          {skipMarkup}
          {topBarMarkup}
          <Container
            size="full"
            spacing="none"
            padding={
              layoutContainsNavigation
                ? { horizontal: { size: 'md', disableOnMobile: true } }
                : null
            }
          >
            <Container
              size={layoutContainsNavigation ? 'xl' : 'full'}
              direction="grid"
              containerProps={navigationAttributes}
            >
              {layoutContainsMobileNavigation && (
                <MobileNavigationMarkup
                  mobileNavShowing={mobileNavShowing}
                  handleNavigationDismiss={this.handleNavigationDismiss}
                  content={
                    <>
                      {navigation}
                      {navigationSecondaryMarkup}
                    </>
                  }
                />
              )}
              {layoutContainsNavigation && desktopNavigationMarkup}
              <Container
                as="main"
                size={layoutContainsNavigation ? 'lg' : 'full'}
                spacing="none"
                containerProps={{
                  id: 'FrameMain',
                }}
              >
                {children}
              </Container>
            </Container>
          </Container>
        </Container>
      </FrameContext.Provider>
    )
  }

  private handleFocus = () => {
    this.setState({ skipFocused: true })
  }

  private handleBlur = () => {
    this.setState({ skipFocused: false })
  }

  private handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    const { skipToContentTarget } = this.props
    if (skipToContentTarget && skipToContentTarget.current) {
      skipToContentTarget.current.focus()
      event?.preventDefault()
    }
  }

  private handleNavigationDismiss = () => {
    const { onNavigationDismiss } = this.props
    if (onNavigationDismiss != null) {
      onNavigationDismiss()
    }
  }

  private handleNavKeydown = (
    event: React.KeyboardEvent<HTMLElement>,
  ): void => {
    const { key } = event
    const {
      mediaQuery: { isNavigationCollapsed: mobileSidebarEnabled },
      mobileSidebarOpen,
    } = this.props

    const mobileNavShowing = mobileSidebarEnabled && mobileSidebarOpen
    if (mobileNavShowing && key === 'Escape') {
      this.handleNavigationDismiss()
    }
  }
}

export function Frame(props: FrameProps) {
  const mediaQuery = useMediaQuery()

  return <FrameInner {...props} mediaQuery={mediaQuery} />
}
